import React from 'react';
import { Battery, Zap, Clock, AlertCircle, CheckCircle, Power, IndianRupee, Activity } from 'lucide-react';
import Navbar from '../components/Navbar';
import { formatINR } from "../utils/formatINR";

const ActiveSessions = () => {
  const stations = [
    {
      id: '11',
      name: 'Fast Charging Station',
      location: '88/6 Aishwarya layout',
      totalRevenue: 2840.5,
      totalSessions: 127,
      guns: [
        {
          id: 'L',
          status: 'charging',
          power: 150,
          priceSoFar: 45.80,
          usage: 85,
          vehicle: 'Tetra Car'
        },
        {
          id: 'R',
          status: 'available',
          power: 150,
          priceSoFar: 0.00,
          usage: 0,
          vehicle: null
        }
      ]
    },
    {
      id: '12',
      name: 'Electronic city Super Charger',
      location: 'Electronic city',
      totalRevenue: 1580.25,
      totalSessions: 89,
      guns: [
        {
          id: 'L',
          status: 'charging',
          power: 50,
          priceSoFar: 28.40,
          usage: 60,
          vehicle: 'Mahindra BE9'
        },
        {
          id: 'R',
          status: 'fault',
          power: 0,
          priceSoFar: 0,
          usage: 0,
          vehicle: null
        }
      ]
    }
  ];

  const overallStats = {
    activeStations: 3,
    totalRevenue: 9441.75,
    totalSessions: 139,
    stationsOnline: 4
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'charging': return 'text-black';
      case 'available': return 'text-gray-600';
      case 'fault': return 'text-gray-800';
      default: return 'text-gray-600';
    }
  };

  const getStatusDot = (status) => {
    switch (status) {
      case 'charging': return 'bg-black';
      case 'available': return 'bg-gray-400';
      case 'fault': return 'bg-gray-600';
      default: return 'bg-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-black p-6">
        {/* <Navbar/> */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div className="relative">
            <h1 className="text-2xl font-semibold tracking-tight text-ink mb-2">
              Active Sessions
            </h1>
            <div className="absolute -bottom-1 left-0 w-16 h-0.5 bg-black"></div>
            <p className="text-gray-600 mt-3">Real-time charging station monitoring</p>
          </div>
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
            <div className="w-2 h-2 bg-black rounded-full animate-pulse"></div>
            <span className="text-sm text-black font-medium">Live Data</span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="group bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium mb-1 uppercase tracking-wide">Active Stations</p>
                <p className="text-2xl font-bold text-black">
                  {overallStats.activeStations}
                </p>
              </div>
              <div className="text-black bg-gray-100 p-3 rounded-lg group-hover:bg-gray-200 transition-colors duration-300">
                <Zap className="w-5 h-5" />
              </div>
            </div>
          </div>
          
          <div className="group bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium mb-1 uppercase tracking-wide">Total Revenue</p>
                <p className="text-2xl font-bold text-black">
                  {formatINR(overallStats.totalRevenue)}
                </p>
              </div>
              <div className="text-black bg-gray-100 p-3 rounded-lg group-hover:bg-gray-200 transition-colors duration-300">
                <IndianRupee className="w-5 h-5" />
              </div>
            </div>
          </div>
          
          <div className="group bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium mb-1 uppercase tracking-wide">Total Sessions</p>
                <p className="text-2xl font-bold text-black">
                  {overallStats.totalSessions}
                </p>
              </div>
              <div className="text-black bg-gray-100 p-3 rounded-lg group-hover:bg-gray-200 transition-colors duration-300">
                <Battery className="w-5 h-5" />
              </div>
            </div>
          </div>
          
          <div className="group bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium mb-1 uppercase tracking-wide">Stations Online</p>
                <p className="text-2xl font-bold text-black">
                  {overallStats.stationsOnline}
                </p>
              </div>
              <div className="text-black bg-gray-100 p-3 rounded-lg group-hover:bg-gray-200 transition-colors duration-300">
                <Power className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>

        {/* Station Cards */}
        <div className="space-y-6">
          {stations.map((station, index) => (
            <div key={station.id} className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-500 hover:-translate-y-1 overflow-hidden">
              {/* Station Header */}
              <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <h2 className="text-xl font-bold text-black">
                      {station.name}
                    </h2>
                    <p className="text-gray-600">{station.location}</p>
                    <p className="text-gray-500 text-sm font-mono bg-gray-100 px-2 py-1 rounded inline-block">
                      ID: {station.id}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-600 text-sm font-medium mb-1">Total Revenue</p>
                    <p className="text-2xl font-bold text-black">
                      {formatINR(station.totalRevenue)}
                    </p>
                    <p className="text-gray-500 text-sm">
                      {station.totalSessions} sessions
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Connector Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                {station.guns.map((gun, gunIndex) => (
                  <div key={`${station.id}-${gun.id}`} className={`p-6 bg-white hover:bg-gray-50 transition-colors duration-300 ${gunIndex === 0 ? 'md:border-r border-gray-100' : ''}`}>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-black">
                        Connector {gun.id}
                      </h3>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getStatusDot(gun.status)} ${gun.status === 'charging' ? 'animate-pulse' : ''}`}></div>
                        <span className={`text-sm capitalize font-medium px-2 py-1 rounded-full ${gun.status === 'charging' ? 'bg-black text-white' : gun.status === 'fault' ? 'bg-gray-200 text-gray-800' : 'bg-gray-100 text-gray-600'}`}>
                          {gun.status}
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-700 font-medium">Power Available</span>
                        <span className="text-black font-semibold">{gun.power}kW</span>
                      </div>
                      
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-700 font-medium">Price So Far</span>
                        <span className="text-black font-bold">{formatINR(gun.priceSoFar)}</span>
                      </div>
                      
                      {gun.vehicle && (
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-700 font-medium">Vehicle</span>
                          <span className="text-black font-semibold">{gun.vehicle}</span>
                        </div>
                      )}
                      
                      {gun.status === 'charging' && (
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-700 font-medium">Progress</span>
                            <span className="text-black font-semibold">{gun.usage}%</span>
                          </div>
                          <div className="relative">
                            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                              <div 
                                className="bg-black h-3 rounded-full transition-all duration-1000 ease-out"
                                style={{ width: `${gun.usage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-gray-600 text-sm bg-white px-4 py-2 rounded-full border border-gray-200 shadow-sm">
            <Activity className="w-4 h-4" />
            <span>Dashboard updated in real-time</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveSessions;